import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { type UserCompany } from '../user';

import { queryKeysCompany } from './queryKeys';

type CreateCompanyResponse = {
  code: number;
  id: UUID;
  message: string;
};

/**
 * Creates a company.
 *
 * @param {UserCompany} companyData - An object containing the company details to be created.
 * @returns {Promise<CreateCompanyResponse>} - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/create_company_company_post
 */
export const createCompany = async (companyData: UserCompany) => {
  const response = await vestigasApi
    .post(ENDPOINT.COMPANY.CREATE(), {
      json: companyData,
    })
    .json<CreateCompanyResponse>();

  return response;
};

/**
 * Custom hook for creating a company using mutations.
 *
 * @returns {Object} An object containing the mutation function and other properties.
 */
export const useMutationCreateCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (companyData: UserCompany) => createCompany(companyData),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: queryKeysCompany.getAll({}),
      });
    },
  });
};
